import { tryCatch } from "../../utils/typescript/try-catch.ts";
import { DomService } from "../../utils/typescript/service/dom.service.ts";

type ConditionsResult = {
  privacyPolicyVersion: string | null;
  privacyPolicyContent: string | null;
  termsAndConditionsVersion: string;
  termsAndConditionsContent: string;
};

const loginForms: NodeListOf<HTMLFormElement> = document.querySelectorAll(
  "[data-login-form-v3]",
);
loginForms.forEach((form) => {
  const email: HTMLInputElement | null = form.querySelector(
    "input[name='email']",
  );
  const password: HTMLInputElement | null = form.querySelector(
    "input[name='password']",
  );
  const termsDialog: HTMLDialogElement | null = document.querySelector(
    "#dialog-login-terms",
  );

  if (!email || !password || !termsDialog)
    return console.error("Email, password or dialog not found for terms.");

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const conditions = await getConditions(email.value, password.value);

    // Login User if no new conditions are present
    if (!conditions) return form.submit();

    await handleNewTermsSubmission(termsDialog, conditions, form);
  });
});

// HELPERS
async function getConditions(
  email: string,
  password: string,
): Promise<void | ConditionsResult> {
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ email, password }),
  } satisfies RequestInit;

  const { data, error } = await tryCatch(fetch("/user/condition", options));
  if (error) return console.error(`Error fetching conditions: ${error}`);
  if (!data.ok)
    throw new Error(`Endpoint error for /user/condition: ${data.status}`);

  const response = await data.text();
  return response === "" ? undefined : JSON.parse(response); // Terms can be an empty string if no new terms are present for the user
}

function showTermsDialog(
  dialog: HTMLDialogElement,
  conditions: ConditionsResult,
) {
  // Dialog components
  const modalContent: HTMLElement | null =
    dialog.querySelector("[data-content]");

  if (!modalContent) return console.error("Modal content not found.");

  // Get the wrapper. The wrapper is only used for styling the child elements coming from /user/conditions.
  // @see terms/wrapper.ftlh component
  const termsWrapper = modalContent.querySelector("[data-terms-wrapper]");
  if (!termsWrapper) return console.error("Terms wrapper not found.");

  termsWrapper.innerHTML = DomService.removeScriptsFromString(
    conditions.termsAndConditionsContent,
  ); // Set content from response (with removed scripts)

  // Handle scrolling of the body
  document.body.classList.add("noscroll");
  dialog.addEventListener("close", () => {
    document.body.classList.remove("noscroll");
  });

  dialog.showModal();
}

function getUserHasAcceptedTerms(termsDialog: HTMLDialogElement) {
  const acceptTermsButton: HTMLButtonElement | null = termsDialog.querySelector(
    "button[name=accept-terms-button]",
  );

  return new Promise<void>((resolve) => {
    if (!acceptTermsButton) {
      console.error("Accept terms button not found.");
      return resolve();
    }

    acceptTermsButton.addEventListener(
      "click",
      () => {
        acceptTermsButton.disabled = true; // Disable button to prevent multiple clicks
        return resolve();
      },
      {
        once: true,
      },
    );
  });
}

async function handleNewTermsSubmission(
  termsDialog: HTMLDialogElement,
  conditions: ConditionsResult,
  form: HTMLFormElement,
) {
  showTermsDialog(termsDialog, conditions);
  await getUserHasAcceptedTerms(termsDialog);

  const acceptTermsInput = document.createElement("input");
  acceptTermsInput.setAttribute("type", "hidden");
  acceptTermsInput.setAttribute("name", "acceptTerms");
  acceptTermsInput.setAttribute("value", "true");
  form.append(acceptTermsInput);

  form.submit();
}
